import { EventEmitter } from 'events';

import * as $ from 'jquery';

import { evaluateXpath, HtmlPreviewDriver } from 'src/integration/api/ui/annotators/web/HtmlPreviewDriver';
import { IStyleBlock } from 'src/integration/api/ui/InstructorFeedback';
import { findNodeByXpath } from 'src/shared/DomUtil';
import * as NullUtil from 'src/shared/NullUtil';
import { waitUntil } from 'src/shared/PromiseUtil';
import { IEditorController } from 'src/shared/wysiwyg/IEditorController';
import { BlockProcessorService } from 'src/shared/wysiwyg/ultra/UltraBlockProcessing/BlockProcessorService';
import { IEditorBlock } from 'src/shared/wysiwyg/ultra/UltraBlockProcessing/UltraBlockInterfaces';
import { UltraContentEditableFilter } from 'src/shared/wysiwyg/ultra/UltraContentEditableFilter';
import {
    applyWysiwygFix,
    extractContextAroundImage,
    FixOptions,
    processLearnImageAndGetDisplayableElement,
    styleAttributes
} from 'src/shared/wysiwyg/WysiwygFeedbackUtil';

import { getUltraDocumentContainer } from './get-ultra-document-container';

export const nonInteractableElements = [
    '.js-file-viewer-attachment-widgets',
    '.document-block-controls',
    '.document-row-controls',
    '.document-column-resize-handle'
];

export interface IRteStyleBlock {
    level: number;
    name: string;
    tag: string;
    style?: Record<string, string>[];
    className?: string;
}

export const GET_BLOCKS_CALL = 'getBlocks';

export class UltraEditorController implements IEditorController {
    private blockProcessorService: BlockProcessorService;
    private emitter = new EventEmitter();
    public documentObserver?: MutationObserver;
    private headerObserver?: MutationObserver;
    private headerElement?: HTMLElement;
    private emittedContent?: string;
    private indicator?: HTMLElement;
    private previewDriver?: HtmlPreviewDriver;
    private quickFixInProgress = false;
    private blockStyles: IStyleBlock[] = [];
    private deactivated = false;

    private readonly ATTRIBUTES_TO_OBSERVE = ['alt', 'class', 'style', 'href'];

    constructor(
        private readonly baseUrl: string,
        private readonly i18n: any,
        private readonly container: HTMLElement,
        private readonly jq: JQueryStatic = (window as any).jQuery ? (window as any).jQuery : $
    ) {
        this.blockProcessorService = new BlockProcessorService(container);
    }

    public async onContentInit(cb: (content: string) => void): Promise<void> {
        try {
            const blocks = await this.fetchEditorBlocks();
            cb(await this.processEditorBlockContent(blocks));
        } catch (error) {
            throw new Error('Error initializing UltraEditorController');
        }
    }

    private fetchAndProcessEditorContent(): void {
        this.fetchEditorBlocks()
            .then(blocks => this.processEditorBlockContent(blocks))
            .then(content => {
                if (content !== this.emittedContent) {
                    this.emittedContent = content;
                    this.emitter.emit('content-changed', content);
                }
                this.markNonInteractableElementsAsInactive();
            })
            .catch(error => console.error('Error fetching editor blocks', error));
    }

    protected async fetchEditorBlocks(): Promise<IEditorBlock[]> {
        return new Promise((resolve, reject) => {
            this.getBbDocumentEditor().trigger(
                'api',
                [GET_BLOCKS_CALL, (blocks: IEditorBlock[]) => resolve(blocks), () => reject(new Error('API call failed'))]
            );
        });
    }

    private async processEditorBlockContent(blocks: IEditorBlock[]): Promise<string> {
        if (NullUtil.hasValue(blocks)) {
            return await this.blockProcessorService.processEditorBlockContent(blocks);
        } else {
            return '';
        }
    }

    public getFeedbackContainer(): HTMLElement | undefined {
        return $(this.container).parents('.document-content-root')[0];
    }

    public enableFeedbackMode(instructorFeedbackWindow: Window): void {
        $(this.container).addClass('ally-wysiwyg-feedback-quill-fullscreen');
        nonInteractableElements.forEach(s => $(this.container).find(s).addClass('ally-wysiwyg-feedback-list-item-inactive'));
        this.previewDriver = new HtmlPreviewDriver(
            this.baseUrl,
            this.i18n,
            { 'contentContainer': $(this.container)[0], 'scrollContainer': $(this.container)[0] },
            this.onSelection.bind(this)
        );
        this.previewDriver.start({
                'highlightInterval': 500,
                'parentWindow': instructorFeedbackWindow,
                'preprocessInterval': false
        });
    }

    public disableFeedbackMode(): void {
        $(this.container).removeClass('ally-wysiwyg-feedback-quill-fullscreen');
        this.previewDriver?.destroy();
        this.previewDriver = undefined;
        nonInteractableElements.forEach(s => $(this.container).find(s).removeClass('ally-wysiwyg-feedback-list-item-inactive'));
        const bbDocumentEditor = this.getBbDocumentEditor().get(0);
        if (bbDocumentEditor) {
            bbDocumentEditor.dispatchEvent(new MouseEvent('click', { 'bubbles': true, 'cancelable': true }));
        }
        this.bringFocusToScoreMeter();
    }

    private bringFocusToScoreMeter(): void {
        const scoreMeterSelector = '.ally-score-meter-container';
        waitUntil(
            () => Promise.resolve(document.querySelector(scoreMeterSelector) !== null),
            10,
            10,
            'focusOnScoreMeter',
            false
            ).then(() => {
                const scoreMeter = document.querySelector(scoreMeterSelector) as HTMLElement;
                if (scoreMeter) {
                    scoreMeter.focus();
                }
            }).catch(() => { return; });
    }

    /**
     * Add the socre meter to the document header/title element. Note we need to disconeect the observer as we are editing
     * the element we are observing and it will trigger the observer.
     * @param indicatorContainer
     */
    public addScoreMeter(indicatorContainer: HTMLElement): void {
        const actionsMenu = getUltraDocumentContainer(this.container).find('.document-header-actions');
        if (actionsMenu.find('ally-wysiwyg-feedback-quill').length === 0) {
            this.headerObserver?.disconnect();
            actionsMenu.prepend(indicatorContainer);
            this.headerElement && this.observeHeaderChanges(this.headerElement);
        }
    }

    public getBbDocumentEditor(): JQLite {
        return this.jq(this.container).parents('.document-body');
    }

    private async applyFixProcess(
        ruleName: string,
        editorLocators: string[],
        fixOptions: FixOptions,
        root: HTMLElement
    ): Promise<void> {
        const targetElements: HTMLElement[] = [];
        for (const locator of editorLocators) {
            const element = findNodeByXpath(document, root, locator) as HTMLElement;
            if (!element) continue;
            let targetElement: HTMLElement | null = element;
            while (targetElement && !targetElement.classList.contains('ql-container')) {
                targetElement = targetElement.parentElement;
            }
            if (!targetElement) continue;
            targetElements.push(targetElement);
            targetElement.click();
            await new Promise(resolve => requestAnimationFrame(resolve));
        }
        applyWysiwygFix(ruleName, editorLocators, fixOptions, root);
        for (const targetElement of targetElements) {
            targetElement.blur();
            await new Promise(resolve => requestAnimationFrame(resolve));
        }
        this.fetchAndProcessEditorContent();
        this.previewDriver?.resetSelection();
    }

    private async onSelection(issue?: HTMLElement): Promise<void> {
        if (NullUtil.hasValue(issue) && (!$.contains(this.container, issue))) {
            console.warn('Issue element is no longer attached to Quill DOM', issue);
            this.emitter.emit('selection-changed', undefined);
            return;
        }
        try {
            const element = await processLearnImageAndGetDisplayableElement(issue);
            this.emitEvent('selection-changed', element);
        } catch (e) {
            /* istanbul ignore next */
            console.error('Error processing image', e);
        }
    }

    private markNonInteractableElementsAsInactive(): void {
        if (!this.container.closest('.ally-wysiwyg-feedback-quill-fullscreen')) {
            return;
        }
        this.documentObserver?.disconnect();
        nonInteractableElements.forEach(selector => {
            const elements = this.container.querySelectorAll(selector);
            elements.forEach(element => element.classList.add('ally-wysiwyg-feedback-list-item-inactive'));
        });
        requestAnimationFrame(() => this.observeContentChanges());
    }

    public async activate(indicator: HTMLElement): Promise<void> {
        this.blockStyles =
            await new Promise<IStyleBlock[]>(
                resolve => this.getBbDocumentEditor().trigger(
                    'api',
                    ['blockStyles', (blockStyles: IRteStyleBlock[]) => resolve(this.parseBlockStyles(blockStyles))])
        ).catch(e => {
            console.warn('Failed to retrieve block styles', e);
            return [];
        });
        $(this.container).addClass('ally-wysiwyg-feedback-quill');
        this.indicator = indicator;
        this.indicator.classList.add('button', 'button--secondary', 'ally-score-meter-container-quill');
        this.addScoreMeter(this.indicator);
        this.initHeaderMutationObserver();
        this.initDocumentMutationObserver();
    }

    /**
     * Watch the header element in the Ultra Document as when the user changes the document title it refreshes
     * the element, removing our indicator. Unfortuneately, there is no useful class or attribute on this element so
     * we start from one we can find reliably and walk up the DOM tree. This is a bit fragile, but it's the best we can do.
     * @private
     */
    private initHeaderMutationObserver(): void {
        if (this.headerObserver) return;
        this.headerObserver = new MutationObserver(() => {
            this.indicator && this.addScoreMeter(this.indicator);
        });
        const container = getUltraDocumentContainer(this.container)[0];
        if (container) {
            // Create a MutationObserver to monitor changes in the container's DOM structure.
            // This is used to dynamically detect when the header element is added to the DOM.
            const observer = new MutationObserver(() => {
                // Search for the header element using a specific data-analytics-id attribute.
                const headerElement = container.querySelector('[data-analytics-id="document.panel.enter-edit-mode-header"]');
                if (!headerElement) {
                    return; // Exit early to avoid further execution of this callback.
                }

                // Traverse up the DOM tree to find the parent element of the header.
                const headerParent = headerElement?.parentElement?.parentElement as HTMLElement | undefined;
                if (!(headerParent instanceof HTMLElement)) {
                    return; // Exit early to avoid further execution of this callback.
                }
                this.headerElement = headerParent;
                this.indicator && this.addScoreMeter(this.indicator);

                // Start observing changes to the header element to monitor title changes.
                this.observeHeaderChanges(this.headerElement);

                // Disconnect the MutationObserver once the header element is found.
                // This prevents unnecessary monitoring and improves performance.
                observer.disconnect();
            });

            // Start observing the container for changes in its child elements and subtree.
            // This ensures that the MutationObserver detects when the header element is added to the DOM.
            observer.observe(container, { 'childList': true, 'subtree': true });
        }
    }

    private observeHeaderChanges(headerElement: Node): void {
        this.headerObserver?.observe(headerElement, {
            'attributeFilter': ['class'],
            'attributes': true,
            'childList': false,
            'subtree': true
        });
    }

    private initDocumentMutationObserver(): void {
        if (this.documentObserver) return;
        this.documentObserver = new MutationObserver(() => {
            this.fetchAndProcessEditorContent();
        });
        this.observeContentChanges();
    }

    private observeContentChanges(): void {
        this.documentObserver?.observe(this.getContentContainer(), {
            'attributeFilter': this.ATTRIBUTES_TO_OBSERVE,
            'attributes': true,
            'characterData': true,
            'childList': true,
            'subtree': true
        });
    }

    public async applyFix(ruleName: string, locators: string[], fixOptions: FixOptions): Promise<void> {
        if (this.quickFixInProgress) {
            return;
        }
        // remove the observer to prevent it from triggering while we are applying the fix
        this.documentObserver?.disconnect();
        const root = $(this.container).find('.ql-editor')[0];
        const editorBlockIssues = this.mapEditorBlockIssues(locators);
        try {
            await this.processEditorIssues(editorBlockIssues, ruleName, fixOptions, root);
        } finally {
            this.quickFixInProgress = false;
            // Reconnect the observer to continue observing content changes.
            requestAnimationFrame(() => this.observeContentChanges());
        }
    }

    private mapEditorBlockIssues(locators: string[]): Map<string, string[]> {
        return locators.reduce((map, xpath) => {
            let id = this.parseEditorId(xpath);
            if (NullUtil.hasValue(id)) {
                id = id.replace(/-rte$/g, '');
                if (!map.has(id)) {
                    map.set(id, []);
                }
                map.get(id)!.push(xpath);
            }
            return map;
        }, new Map<string, string[]>());
    }

    private async processEditorIssues(
        editorBlockIssues: Map<string, string[]>,
        ruleName: string,
        fixOptions: FixOptions,
        root: HTMLElement
    ): Promise<void> {
        this.quickFixInProgress = true;
        for (const editorId of Array.from(editorBlockIssues.keys())) {
            const editorLocators = NullUtil.orElse(editorBlockIssues.get(editorId), []);
            let issue: HTMLElement | undefined;
            for (const locator of editorLocators) {
                issue = evaluateXpath(locator, document, this.getContentContainer());
                if (NullUtil.hasValue(issue)) {
                    break;
                }
            }
            if (!NullUtil.hasValue(issue)) {
                continue;
            }
            await this.applyFixProcess(ruleName, editorLocators, fixOptions, root);
        }
    }

    public belongsToEditor(el: HTMLElement): boolean {
        return $.contains(this.container, el) || $(el).is('.ms-Dialog') || $(el).parents('.ms-Dialog').length > 0;
    }

    public deactivate(): void {
        this.deactivated = true;
        this.emitter.removeAllListeners();
        this.disableFeedbackMode();
        this.getBbDocumentEditor().unbind('api.onRteChange');
        if (NullUtil.hasValue(this.indicator)) {
            this.indicator.remove();
            this.indicator = undefined;
        }
        $(this.container).removeClass('ally-wysiwyg-feedback-quill');
        this.headerObserver && this.headerObserver.disconnect();
        this.documentObserver && this.documentObserver.disconnect();
        this.headerObserver = this.documentObserver = undefined;
    }

    public isDeactivated(): boolean {
        return this.deactivated;
    }

    public onContentChange(cb: (content: string) => void): void {
        this.emitter.on('content-changed', cb);
    }

    public onForceDisableFeedbackMode(cb: () => void): void {
        this.emitter.on('force-disable-feedback-mode', cb);
    }

    public getStyleBlocks(): IStyleBlock[] {
        return this.blockStyles;
    }

    public emitEvent(event: string, ...args: any[]): void {
        this.emitter.emit(event, ...args);
    }

    public onSelectionChange(cb: (content: string) => void): void {
        this.emitter.on('selection-changed', cb);
    }

    public getContentContainer(): HTMLElement {
        return $(this.container)[0];
    }

    public getEditableEditors(): HTMLElement[] {
        return $(this.container).find('.ql-editor').toArray().filter(UltraContentEditableFilter.getContentEditableFilter());
    }

    public anyContentEditable(): boolean {
        return this.getEditableEditors().length > 0;
    }

    private parseBlockStyles(rteBlockStyles: IRteStyleBlock[]): IStyleBlock[] {
        return rteBlockStyles.map(({ name, tag, level, style = [], className }) => {
            const elemStyle: { [_: string]: string } = {};
            if (style.length > 0) {
                style.forEach(r => Object.keys(r).forEach(k => elemStyle[k] = r[k]));
            } else if (NullUtil.hasValue(className)) {
                const elem = $(`<ally-mock style="display: none" class="bb-editor-toolbar-button ${className}"></ally-mock>`);
                $(this.container).find('.bb-editor-toolbar').prepend(elem);
                styleAttributes.forEach(a => elemStyle[a] = elem.css(a));
                elem.detach();
            }
            return { level, name, 'style': elemStyle, tag };
        });
    }

    private parseEditorId(xpath: string): string | undefined {
        return (NullUtil.orElse<string[]>((/\/\/.*\[@id='(.*)']/g).exec(xpath), []))[1];
    }

    public extractImageContext(locator: { xpath: string }): string | undefined {
        const root = $(this.container).find('.ql-editor')[0];
        return extractContextAroundImage(root, locator.xpath);
    }
}
